import { differenceInDays } from 'date-fns';

import { CONTRACT_IS_CONSIDERED_NEW_DAYS } from '@constants/index';
import { Contract, ContractStatusCode } from '@models/contract.types';

export type ContractStatusFields = Pick<
  Contract,
  | 'startDate'
  | 'statusCodeOfContract'
  | 'originalContractId'
  | 'replacingContractId'
>;

/** Contract is new when it is starting or at most 10 days from the start date */
export const isContractNewOrUpcoming = (
  contract: ContractStatusFields | null | undefined,
  now = new Date()
) => {
  const startDate = contract?.startDate;

  // Don't consider contract new if it superseds an old contract.
  const isReplacingContract = !!contract?.originalContractId;

  if (!startDate || isReplacingContract) return false;

  // How many days from now does the contract start? If positive, start date is in the future.
  const daysFromNow = differenceInDays(new Date(startDate), now);

  return daysFromNow >= -CONTRACT_IS_CONSIDERED_NEW_DAYS;
};

export const isContractTerminated = (
  contract: ContractStatusFields | null | undefined
) => {
  // Don't consider contract as terminated if there is a contract that will supersed the current one.
  const isContractAboutToBeReplaced = !!contract?.replacingContractId;

  return (
    contract?.statusCodeOfContract === ContractStatusCode.RESIGNED_STATUS &&
    !isContractAboutToBeReplaced
  );
};

type ApartmentWithContract = {
  apartmentId: string;
  contract: ContractStatusFields | null | undefined;
};

/**
 * For a home changer, i.e. a user moving from one Sato apartment to another, gets the apartment they are
 * moving to or from, depending on which one is selected.
 */
export const getOtherApartmentIdInHomeChange = (
  selectedContract: ContractStatusFields | null | undefined,
  otherApartments: ApartmentWithContract[]
) => {
  // A single other apartment is most likely the one the user is moving to or from, so contract data is not needed
  if (otherApartments.length === 1) return otherApartments[0]!.apartmentId;

  // If the selected contract is terminated, the user is moving out, so look for their new home.
  // If it is new, the user is moving in, so look for the home they are leaving.
  const isCounterpart = (contract: ContractStatusFields | null | undefined) => {
    if (isContractTerminated(selectedContract)) {
      return isContractNewOrUpcoming(contract);
    }

    if (isContractNewOrUpcoming(selectedContract)) {
      return isContractTerminated(contract);
    }

    return false;
  };

  // Several counterparts are very unlikely; pick the first one
  return otherApartments.find(({ contract }) => isCounterpart(contract))
    ?.apartmentId;
};
